import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { LEGAL_CONTENT } from "@/shared/constants/legalContent";
import { formatLegalContent } from "@/shared/utils/formatLegalContent";

export default function LegalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || 'privacy';

  const handleBack = () => {
    navigate('/');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const renderContent = () => {
    const currentContent = LEGAL_CONTENT[section as keyof typeof LEGAL_CONTENT];

    if (!currentContent) {
      return (
        <div className="space-y-6">
          <h1 className="text-4xl font-bold text-foreground">Content Not Found</h1>
          <p className="text-muted-foreground">
            The requested content could not be found.
          </p>
        </div>
      );
    }

    const htmlContent = currentContent.formattedHTML || formatLegalContent(currentContent.content, section as 'privacy' | 'terms' | 'disclaimer');

    return (
      <div className="space-y-6">
        <h1 className="text-4xl font-bold text-foreground">{currentContent.title}</h1>
        <div className="max-w-none prose prose-slate dark:prose-invert prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-muted-foreground prose-p:leading-relaxed prose-li:text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
          {currentContent.lastUpdated && (
            <p className="mb-6 text-sm text-muted-foreground"><strong>Last updated:</strong> {currentContent.lastUpdated}</p>
          )}
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button onClick={handleBack} variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                }}
                className="text-2xl font-bold text-primary hover:text-primary/90 transition-colors"
              >
                wlkthru
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Navigation */}
            <Card className="p-4 mb-8">
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => {
                    navigate('/legal?section=privacy');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  variant={section === 'privacy' ? 'default' : 'outline'}
                  size="sm"
                >
                  Privacy Policy
                </Button>
                <Button 
                  onClick={() => {
                    navigate('/legal?section=terms');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  variant={section === 'terms' ? 'default' : 'outline'}
                  size="sm"
                >
                  Terms and Conditions
                </Button>
                <Button 
                  onClick={() => {
                    navigate('/legal?section=disclaimer');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  variant={section === 'disclaimer' ? 'default' : 'outline'}
                  size="sm"
                >
                  Disclaimer
                </Button>
              </div>
            </Card>

            {/* Content */}
            <Card className="p-8">
              {renderContent()}
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-background border-t border-border py-8">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-muted-foreground text-sm">
              © 2025 wlkthru. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}